<script lang="ts">
  import { onMount } from 'svelte';
  
  export let options: Array<{uuid: string, name: string}> = [];
  export let selectedValue: string = '';
  export let placeholder: string = 'Select...';
  export let id: string | undefined = undefined;
  export let onSelectionChange: (value: string) => void = () => {};
  export let disabled: boolean = false;
  
  let isOpen = false;
  let searchInput = '';
  let dropdownRef: HTMLDivElement;
  
  // Update selectedOption when options or selectedValue changes
  $: selectedOption = options.find(opt => opt.uuid === selectedValue);
  $: searchInput = selectedOption ? selectedOption.name : '';
  
  $: filteredOptions = options.filter(option => 
    option.name.toLowerCase().includes(searchInput.toLowerCase())
  );
  
  function handleSelect(option: {uuid: string, name: string}) {
    selectedValue = option.uuid;
    searchInput = option.name;
    isOpen = false;
    onSelectionChange(option.uuid);
  }
  
  function handleInputFocus() {
    isOpen = true;
    searchInput = ''; // Clear search on focus to show all options
  }
  
  function handleInputChange() {
    isOpen = true;
    if (!searchInput) {
      selectedValue = '';
      onSelectionChange('');
    }
  }
  
  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      isOpen = false;
      // Restore the selected value if there is one, otherwise clear the input
      searchInput = selectedOption ? selectedOption.name : '';
    }
  }
  
  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });
</script>

<div class="dropdown-container" bind:this={dropdownRef}>
  <div class="input-wrapper">
    <input
      {id}
      type="text"
      class="dropdown-input"
      placeholder={placeholder}
      bind:value={searchInput}
      on:focus={handleInputFocus}
      on:input={handleInputChange}
      {disabled}
    />
    <div class="dropdown-icon" class:open={isOpen}>
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
      <path d="M7 10l5 5 5-5z"/>
    </svg>
  </div>
  </div>
  
  {#if isOpen}
    <div class="options-container">
      {#if filteredOptions.length === 0}
        <div class="no-results">No results found</div>
      {:else}
        {#each filteredOptions as option}
          <div
            class="option"
            class:selected={option.uuid === selectedValue}
            on:click={() => handleSelect(option)}
            role="button"
            tabindex="0"
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(option);
              }
            }}
          >
            {option.name}
          </div>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style>
  .dropdown-container {
    position: relative;
    display: inline-block;
    width: 200px;
  }
  
  .input-wrapper {
    position: relative;
    width: 100%;
  }
  
  .dropdown-input {
    width: 100%;
    appearance: none;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 50rem !important;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    font-size: 0.875rem;
    color: #4a5568;
    cursor: text;
    font-weight: 500;
  }
  
  .dropdown-input:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }
  
  .dropdown-input:hover {
    border-color: #cbd5e0;
  }
  
  .dropdown-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    transition: transform 0.2s;
  }
  
  .dropdown-icon.open {
    transform: translateY(-50%) rotate(180deg);
  }
  
  .options-container {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 200px;
    overflow-y: auto;
    z-index: 50;
  }
  
  .option {
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a5568;
    transition: background-color 0.2s;
  }
  
  .option:hover {
    background-color: #f7fafc;
  }
  
  .option.selected {
    background-color: #f0b42911;
    color: #f0b429;
    font-weight: 500;
  }
  
  .option:focus {
    outline: none;
    background-color: #f7fafc;
  }
  
  .no-results {
    padding: 0.75rem 1rem;
    color: #a0aec0;
    font-size: 0.875rem;
    text-align: center;
  }
  
  /* Scrollbar Styling */
  .options-container::-webkit-scrollbar {
    width: 6px;
  }
  
  .options-container::-webkit-scrollbar-track {
    background: #f7fafc;
    border-radius: 3px;
  }
  
  .options-container::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }
  
  .options-container::-webkit-scrollbar-thumb:hover {
    background: #cbd5e0;
  }
</style> 